import { TeammateRoleTypes } from "@repo/app-types/entities/teammatePermissions";
import { buttonStyle, ButtonVariant } from "@repo/ui/Buttons/button";
import { dropdownMenu } from "@repo/ui/Dropdown/dropdownMenuStyles";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import {
  IconArrowBackUp,
  IconArrowForwardUp,
  IconDownload,
  IconExclamationCircle,
  IconPlayerPause,
  IconPlayerPlay,
  IconUser,
  IconVolume,
  IconVolumeOff,
} from "@tabler/icons-react";
import { Duration } from "luxon";
import {
  type FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useKeyboard, useLandmark } from "react-aria";
import {
  Button as AriaButton,
  DialogTrigger,
  Popover,
} from "react-aria-components";
import { selectTeammate } from "../../features/teammate/teammateSlice.js";
import { useAppSelector } from "../../hooks/hook.js";
import { SizedContainer } from "../sizedContainer.js";
import { AudioPlayerContext } from "./context.js";
import { findNextFragmentTime } from "./findNextFragmentTime.js";
import {
  buildLabelForRate,
  findNextPlaybackRate,
  findPreviousPlaybackRate,
  PlaybackRateSelect,
} from "./playbackRateSelect.js";
import { AudioPlayerSourceSelect } from "./sourceSelect.js";
import { useAudioCurrentTime } from "./useAudioCurrentTime.js";
import { VolumeDialog } from "./volumeDialog.js";

export interface AudioPlayerProps {
  /** Hide the source picker when another part of the view owns call navigation. */
  showSourceSelect?: boolean;
}

export const AudioPlayer: FC<AudioPlayerProps> = (props) => {
  const { showSourceSelect = true } = props;
  const context = useContext(AudioPlayerContext);
  const nativeAudio = context.nativeAudio;

  const isLoading = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        nativeAudio?.addEventListener("emptied", onStoreChange);
        nativeAudio?.addEventListener("loadstart", onStoreChange);
        nativeAudio?.addEventListener("loadeddata", onStoreChange);
        nativeAudio?.addEventListener("error", onStoreChange);

        return () => {
          nativeAudio?.removeEventListener("emptied", onStoreChange);
          nativeAudio?.removeEventListener("loadstart", onStoreChange);
          nativeAudio?.removeEventListener("loadeddata", onStoreChange);
          nativeAudio?.removeEventListener("error", onStoreChange);
        };
      },
      [nativeAudio],
    ),
    () =>
      nativeAudio !== null &&
      nativeAudio.readyState < HTMLMediaElement.HAVE_CURRENT_DATA &&
      nativeAudio.networkState === HTMLMediaElement.NETWORK_LOADING,
  );

  const isError = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        nativeAudio?.addEventListener("emptied", onStoreChange);
        nativeAudio?.addEventListener("loadstart", onStoreChange);
        nativeAudio?.addEventListener("error", onStoreChange);

        return () => {
          nativeAudio?.removeEventListener("emptied", onStoreChange);
          nativeAudio?.removeEventListener("loadstart", onStoreChange);
          nativeAudio?.removeEventListener("error", onStoreChange);
        };
      },
      [nativeAudio],
    ),
    () => !!nativeAudio?.error,
  );

  const isPlaying = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        nativeAudio?.addEventListener("emptied", onStoreChange);
        nativeAudio?.addEventListener("play", onStoreChange);
        nativeAudio?.addEventListener("pause", onStoreChange);

        return () => {
          nativeAudio?.removeEventListener("emptied", onStoreChange);
          nativeAudio?.removeEventListener("play", onStoreChange);
          nativeAudio?.removeEventListener("pause", onStoreChange);
        };
      },
      [nativeAudio],
    ),
    () => (nativeAudio ? !nativeAudio.paused : false),
  );

  const currentTime = useAudioCurrentTime(nativeAudio);

  const duration = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        nativeAudio?.addEventListener("emptied", onStoreChange);
        nativeAudio?.addEventListener("durationchange", onStoreChange);

        return () => {
          nativeAudio?.removeEventListener("emptied", onStoreChange);
          nativeAudio?.removeEventListener("durationchange", onStoreChange);
        };
      },
      [nativeAudio],
    ),
    () => nativeAudio?.duration ?? NaN,
  );

  const volume = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        nativeAudio?.addEventListener("volumechange", onStoreChange);

        return () => {
          nativeAudio?.removeEventListener("volumechange", onStoreChange);
        };
      },
      [nativeAudio],
    ),
    () => nativeAudio?.volume ?? NaN,
  );

  const playbackRate = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        nativeAudio?.addEventListener("ratechange", onStoreChange);

        return () => {
          nativeAudio?.removeEventListener("ratechange", onStoreChange);
        };
      },
      [nativeAudio],
    ),
    () => nativeAudio?.playbackRate ?? 1,
  );

  const sliderRef = useRef<HTMLDivElement>(null);

  // Value between 0 and 1 indicating where the audio is playing
  const currentFraction = isFinite(duration) ? currentTime / duration : 0;

  // Value between 0 and 1 indicating where the user is seeking (actively dragging the slider)
  const [seekFraction, setSeekFraction] = useState<number | undefined>(
    undefined,
  );
  const [isDownloading, setIsDownloading] = useState(false);

  const currentTeammate = useAppSelector(selectTeammate);
  const currentTeammateRole = currentTeammate?.permissions.role.type;
  const canDownload =
    currentTeammateRole === TeammateRoleTypes.EDITOR ||
    currentTeammateRole === TeammateRoleTypes.REVIEWER;

  // Value between 0 and 1 indicating what we want to show in the slider
  const sliderFraction = seekFraction ?? currentFraction;

  // Time in seconds indicating where the user is seeking
  const seekTime =
    isFinite(duration) && seekFraction !== undefined
      ? seekFraction * duration
      : undefined;

  // Time in seconds we want to show in the slider
  const sliderTime = seekTime ?? currentTime;

  // TODO: Use Slider component from react-aria-components
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) {
      return;
    }

    let seekFraction: number | undefined;

    const getSeekFraction = (event: MouseEvent) => {
      const rect = slider.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const xFraction = x / rect.width;
      return Math.max(0, Math.min(xFraction, 1));
    };

    const updateSeekFraction = (event: MouseEvent) => {
      if (nativeAudio) {
        seekFraction = getSeekFraction(event);
        setSeekFraction(seekFraction);
      }
    };

    const onMouseDown = (event: MouseEvent) => {
      updateSeekFraction(event);
    };

    const onMouseMove = (event: MouseEvent) => {
      if (seekFraction !== undefined) {
        updateSeekFraction(event);
      }
    };

    const onMouseUp = (event: MouseEvent) => {
      const fraction = getSeekFraction(event);
      seekFraction = undefined;
      setSeekFraction(undefined);

      // Commit the final seek position
      if (nativeAudio && isFinite(nativeAudio.duration)) {
        nativeAudio.currentTime = fraction * nativeAudio.duration;
      }
    };

    const onMouseLeave = () => {
      if (seekFraction === undefined) {
        return;
      }

      const fraction = seekFraction;
      seekFraction = undefined;
      setSeekFraction(undefined);

      // Commit the most recent seek position
      if (nativeAudio && isFinite(nativeAudio.duration)) {
        nativeAudio.currentTime = fraction * nativeAudio.duration;
      }
    };

    slider.addEventListener("mousedown", onMouseDown);
    slider.addEventListener("mousemove", onMouseMove);
    slider.addEventListener("mouseup", onMouseUp);
    slider.addEventListener("mouseleave", onMouseLeave);

    return () => {
      slider.removeEventListener("mousedown", onMouseDown);
      slider.removeEventListener("mousemove", onMouseMove);
      slider.removeEventListener("mouseup", onMouseUp);
      slider.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [nativeAudio]);

  const fragmentTimes = useMemo(
    () =>
      context.sources.find((source) => source.key === context.currentSourceKey)
        ?.fragmentTimes,
    [context.sources, context.currentSourceKey],
  );

  const onPlayPause = () => {
    if (isPlaying) {
      nativeAudio?.pause();
    } else {
      nativeAudio?.play();
    }
  };

  const onRewind = () => {
    if (nativeAudio) {
      nativeAudio.currentTime -= 15;
    }
  };

  const onForward = () => {
    if (!nativeAudio) {
      return;
    }

    const nextFragmentTime = fragmentTimes
      ? findNextFragmentTime(
          fragmentTimes,
          // Round up on the nearest 10 ms so we can skip past discrepancies in audio frames and floating point precision
          Math.ceil(currentTime * 100) / 100,
        )
      : undefined;

    if (nextFragmentTime !== undefined) {
      nativeAudio.currentTime = nextFragmentTime;
    } else {
      nativeAudio.currentTime += 15;
    }
  };

  const onDownload = async () => {
    const currentSource = context.sources.find(
      (source) => source.key === context.currentSourceKey,
    );

    if (!currentSource?.src) {
      return;
    }

    setIsDownloading(true);

    try {
      const response = await fetch(currentSource.src, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(
          `Failed to fetch audio: ${response.status} ${response.statusText}`,
        );
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;

      const baseTitle = currentSource.title?.trim() || "audio";
      const safeTitle = baseTitle.replace(/[\\/:*?"<>|]+/g, "-");
      link.download = safeTitle.endsWith(".mp3")
        ? safeTitle
        : `${safeTitle}.mp3`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // Fallback to opening the URL if fetch is blocked (e.g., CORS) or fails
      // Browser will use server-provided headers/filename if available
      window.open(currentSource.src, "_blank");
    } finally {
      setIsDownloading(false);
    }
  };

  // Make it easy to jump to the audio player for keyboard users (by hitting F6 or using a screen reader)
  const landmarkRef = useRef<HTMLDivElement>(null);
  const { landmarkProps } = useLandmark(
    {
      role: "region",
      "aria-label": "Audio player",
    },
    landmarkRef,
  );

  // Support keyboard shortcuts after jumping to or clicking on the audio player
  // TODO: Maybe make these shortcuts work for the entire dialog?
  const { keyboardProps } = useKeyboard({
    onKeyDown: (event) => {
      if (!event.isDefaultPrevented()) {
        // These keyboard shortcuts match YouTube
        switch (event.key) {
          case " ":
          case "k":
            onPlayPause();
            return;

          case "ArrowLeft":
          case "j":
            onRewind();
            return;

          case "ArrowRight":
          case "l":
            onForward();
            return;

          case "<":
            if (nativeAudio) {
              nativeAudio.playbackRate = findPreviousPlaybackRate(
                nativeAudio.playbackRate,
              );
            }
            return;

          case ">":
            if (nativeAudio) {
              nativeAudio.playbackRate = findNextPlaybackRate(
                nativeAudio.playbackRate,
              );
            }
            return;
        }
      }

      event.continuePropagation();
    },
  });

  const { keyboardProps: thumbKeyboardProps } = useKeyboard({
    onKeyDown: (event) => {
      if (!event.isDefaultPrevented()) {
        switch (event.key) {
          case " ":
            onPlayPause();
            return;

          case "ArrowLeft":
            if (nativeAudio) {
              nativeAudio.currentTime -= 1;
            }
            return;

          case "ArrowRight":
            if (nativeAudio) {
              nativeAudio.currentTime += 1;
            }
            return;
        }
      }

      event.continuePropagation();
    },
  });

  const renderSliderRight = () => {
    if (isError) {
      return <IconExclamationCircle className="size-4 text-destructive" />;
    }

    if (!isFinite(duration) && isLoading) {
      return (
        <div className="size-full animate-pulse rounded-md bg-gray-300"></div>
      );
    }

    if (!isFinite(duration)) {
      return null;
    }

    return (
      <>
        {"-"}
        {Duration.fromMillis(
          Math.abs(duration - Math.floor(sliderTime)) * 1000,
        ).toFormat("h:mm:ss")}
      </>
    );
  };

  return (
    <>
      <div
        ref={landmarkRef}
        className="flex w-full items-center justify-between border-t border-border-light bg-background"
        {...landmarkProps}
        tabIndex={-1}
        {...keyboardProps}
      >
        <div className="max-w-sm flex-auto p-1">
          {showSourceSelect ? (
            <AudioPlayerSourceSelect
              sources={context.sources}
              selectedKey={context.currentSourceKey ?? null}
              onSelectionChange={(key) => {
                if (typeof key === "string") {
                  context.play(key, 0);
                }
              }}
            />
          ) : null}
        </div>

        <div className="flex items-center gap-2 px-8 py-2">
          <PlaybackRateSelect
            playbackRate={playbackRate}
            onPlaybackRateChange={(value) => {
              if (nativeAudio) {
                nativeAudio.playbackRate = value;
              }
            }}
          >
            <AriaButton
              className="flex flex-col items-center gap-0 p-2 text-muted-foreground hover:text-gray-600 disabled:text-gray-400"
              isDisabled={
                !nativeAudio || context.currentSourceKey === undefined
              }
            >
              <SizedContainer
                className="text-center text-xs font-medium"
                childrenForSize="2.0x"
              >
                {buildLabelForRate(playbackRate)}
              </SizedContainer>
            </AriaButton>
          </PlaybackRateSelect>

          <AriaButton
            className="relative p-2 text-muted-foreground hover:text-gray-600 disabled:text-gray-400"
            isDisabled={!nativeAudio || context.currentSourceKey === undefined}
            onPress={onRewind}
          >
            <IconArrowBackUp className="size-5" />
            <span className="absolute bottom-0.5 left-1 text-[10px]">15</span>
          </AriaButton>

          <AriaButton
            className={buttonStyle({
              variant: ButtonVariant.Secondary,
              className: "rounded-full p-2",
            })}
            isDisabled={!nativeAudio || context.currentSourceKey === undefined}
            onPress={onPlayPause}
          >
            <div>
              {isPlaying ? (
                <IconPlayerPause className="size-5" />
              ) : (
                <IconPlayerPlay className="size-5 translate-x-[1px] " />
              )}
            </div>
          </AriaButton>

          <AriaButton
            className="relative p-2 text-muted-foreground hover:text-gray-600 disabled:text-gray-400"
            isDisabled={!nativeAudio || context.currentSourceKey === undefined}
            onPress={onForward}
          >
            <IconArrowForwardUp className="size-5" />
            {fragmentTimes ? (
              <IconUser className="absolute right-1 bottom-1 size-3" />
            ) : (
              <span className="absolute right-1 bottom-0.5 text-[10px]">
                15
              </span>
            )}
          </AriaButton>

          <DialogTrigger>
            <AriaButton
              className="p-2 text-muted-foreground hover:text-gray-600 disabled:text-gray-400"
              isDisabled={
                !nativeAudio || context.currentSourceKey === undefined
              }
            >
              {volume >= 0.01 ? (
                <IconVolume className="size-5" />
              ) : (
                <IconVolumeOff className="size-5" />
              )}
            </AriaButton>

            <Popover className={dropdownMenu().popover()}>
              <VolumeDialog
                volume={volume}
                onVolumeChange={(value) => {
                  if (nativeAudio) {
                    nativeAudio.volume = value;
                  }
                }}
              />
            </Popover>
          </DialogTrigger>

          {canDownload && (
            <AriaButton
              className="p-2 text-muted-foreground hover:text-gray-600 disabled:text-gray-400"
              isDisabled={
                !nativeAudio ||
                context.currentSourceKey === undefined ||
                isDownloading
              }
              onPress={onDownload}
            >
              {isDownloading ? (
                <Loader style={LoaderStyle.SPINNER} />
              ) : (
                <IconDownload className="size-5" />
              )}
            </AriaButton>
          )}
        </div>

        <div className="flex max-w-md flex-auto items-center gap-2 px-2">
          <SizedContainer
            className="text-right font-mono text-xs text-muted-foreground tabular-nums"
            childrenForSize="0:00:00"
          >
            {Duration.fromMillis(Math.floor(sliderTime) * 1000).toFormat(
              "h:mm:ss",
            )}
          </SizedContainer>

          <div
            ref={sliderRef}
            className="group relative mx-[6.5px] inline-flex h-10 w-full min-w-20 cursor-pointer touch-none items-center rounded-md outline-hidden select-none aria-hidden:hidden"
          >
            <div className="relative h-[5px] w-full rounded-full  bg-gray-300 ring-indigo-600 ring-offset-2 group-data-[focus]:ring-2">
              {/* <div className="absolute h-full w-[var(--slider-progress)] bg-indigo-300 will-change-[width]" /> */}
              <div
                className="absolute h-full rounded-full bg-primary will-change-[width]"
                style={{
                  width: `${(sliderFraction * 100).toFixed(1)}%`,
                }}
              />
            </div>
            {isFinite(duration) ? (
              <div
                className="absolute top-1/2 size-[13px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary opacity-0 ring-indigo-600 ring-offset-2 outline-hidden transition-opacity will-change-[left] group-hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2"
                style={{
                  left: `${(sliderFraction * 100).toFixed(1)}%`,
                }}
                // Make the slider thumb focusable and support arrow key presses
                tabIndex={0}
                {...thumbKeyboardProps}
              />
            ) : null}
          </div>

          <SizedContainer
            className="text-left font-mono text-xs text-muted-foreground tabular-nums"
            childrenForSize="-0:00:00"
          >
            {renderSliderRight()}
          </SizedContainer>
        </div>
      </div>
    </>
  );
};
